// PostHog analytics — lazily loaded.
//
// `posthog-js` (with its session-recording extension) is a heavy dependency.
// Importing it statically would pull it into the entry chunk, so every visitor
// — including the logged-out landing page, which deliberately avoids loading
// aws-amplify — would download it before first paint. Instead we `import()` it
// dynamically: Vite emits it as a separate async chunk, and we only fetch and
// initialise it once the browser is idle. All call sites go through the tiny
// `capture` / `identifyUser` / `resetPostHog` wrappers below, which wait for
// the client to be ready before forwarding the call.

import type { PostHog } from 'posthog-js';

const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

let clientPromise: Promise<PostHog | null> | null = null;

// Loads posthog-js (once) and initialises it. Subsequent calls reuse the same
// promise so we never init twice or download the chunk more than once.
function loadClient(): Promise<PostHog | null> {
  if (!key) return Promise.resolve(null);
  if (!clientPromise) {
    clientPromise = import('posthog-js').then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: host,
        // `defaults` opts into PostHog's current recommended defaults, which
        // include autocapture, SPA-aware pageviews, and session recording
        // (the recorder itself is fetched on demand by posthog-js). Session
        // recording must also be enabled in the PostHog project settings.
        defaults: '2026-05-30',
      });
      // dev and prod share one PostHog project, so without this every event
      // from dev.bbxt.org lands indistinguishably alongside real usage — and
      // the E2E smoke suite runs against dev on every single deploy, so that
      // is a steady drip of synthetic traffic into the numbers. Registering it
      // as a super-property stamps `environment` onto every event and session
      // recording, making dev filterable (or excludable) after the fact.
      // A separate PostHog project for dev would be cleaner still.
      posthog.register({ environment: import.meta.env.MODE });
      return posthog;
    });
  }
  return clientPromise;
}

/**
 * Load the client, swallowing any failure.
 *
 * Analytics is never worth an error. Without this, a failed `import()` — a
 * network blip, a content blocker, an offline PWA launch — became an unhandled
 * promise rejection, which Sentry's global handler reports as an application
 * error. That happened in production on iOS Safari: `TypeError: Internal
 * error`, no stack, on the landing page, from a page that was working fine.
 *
 * Rejections were also *sticky*: `clientPromise` caches the rejected promise,
 * so every later capture() re-awaited the same rejection and produced another
 * unhandled one for the rest of the session. Clearing it lets a later call try
 * again, which matters most on the flaky connections this is failing on.
 */
async function safeClient(): Promise<PostHog | null> {
  try {
    return await loadClient();
  } catch (error) {
    // Reset so a later call can retry rather than replaying the failure.
    clientPromise = null;
    console.warn('PostHog unavailable; analytics disabled for now', error);
    return null;
  }
}

// Kicks off loading during browser idle time so analytics never competes with
// first paint. Safe to call before the user interacts.
export function initPostHog() {
  if (!key) return;
  const start = () => {
    void safeClient();
  };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(start, { timeout: 2000 });
  } else {
    setTimeout(start, 1);
  }
}

export function capture(event: string, properties?: Record<string, unknown>) {
  if (!key) return;
  void safeClient().then((client) => client?.capture(event, properties));
}

export function identifyUser(userId: string, email: string) {
  if (!key) return;
  void safeClient().then((client) => client?.identify(userId, { email }));
}

export function resetPostHog() {
  if (!key) return;
  void safeClient().then((client) => client?.reset());
}
